import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  MultiselectCombobox,
  MULTISELECT_COMBOBOX_DEMO_OPTIONS,
  MULTISELECT_COMBOBOX_PREVIEW_STATE_LABELS,
  MULTISELECT_COMBOBOX_PREVIEW_STATES,
  type MultiselectComboboxPreviewState,
} from '@enamelai/design-system'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './MultiselectComboboxShowcase.css'

const ALLERGY_OPTIONS = [
  { value: 'Penicillin', label: 'Penicillin' },
  { value: 'Latex', label: 'Latex' },
  { value: 'Local anesthetic', label: 'Local anesthetic' },
  { value: 'Sulfa drugs', label: 'Sulfa drugs' },
  { value: 'Aspirin', label: 'Aspirin' },
]

const MultiselectComboboxShowcase: React.FC = () => {
  const [procedures, setProcedures] = useState<string[]>(['D8680', 'D1321'])
  const [allergies, setAllergies] = useState<string[]>([])
  const [label, setLabel] = useState('Procedures')
  const [maxSelected, setMaxSelected] = useState('')
  const [disabled, setDisabled] = useState(false)
  const [optional, setOptional] = useState(false)
  const [showTooltip, setShowTooltip] = useState(false)
  const [filterable, setFilterable] = useState(true)
  const [closeOnSelect, setCloseOnSelect] = useState(false)
  const [error, setError] = useState(false)
  const [helperText, setHelperText] = useState('')
  const [highlightedState, setHighlightedState] =
    useState<MultiselectComboboxPreviewState>('default')
  const rowRefs = useRef<
    Partial<Record<MultiselectComboboxPreviewState, HTMLDivElement | null>>
  >({})

  useEffect(() => {
    rowRefs.current[highlightedState]?.scrollIntoView({
      block: 'nearest',
      behavior: 'smooth',
    })
  }, [highlightedState])

  const parsedMaxSelected = useMemo(() => {
    const parsed = Number.parseInt(maxSelected, 10)

    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined
  }, [maxSelected])

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Appearance',
        fields: [
          {
            id: 'multiselect-combobox-highlight-state',
            label: 'Highlighted state',
            control: (
              <select
                id="multiselect-combobox-highlight-state"
                className="ui-field-control"
                value={highlightedState}
                onChange={(event) =>
                  setHighlightedState(
                    event.target.value as MultiselectComboboxPreviewState,
                  )
                }
              >
                {MULTISELECT_COMBOBOX_PREVIEW_STATES.map((option) => (
                  <option key={option} value={option}>
                    {MULTISELECT_COMBOBOX_PREVIEW_STATE_LABELS[option]}
                  </option>
                ))}
              </select>
            ),
          },
          {
            id: 'multiselect-combobox-label',
            label: 'Procedures label',
            control: (
              <input
                id="multiselect-combobox-label"
                className="ui-field-control"
                type="text"
                value={label}
                onChange={(event) => setLabel(event.target.value)}
              />
            ),
          },
          {
            id: 'multiselect-combobox-helper-text',
            label: 'Helper text',
            control: (
              <input
                id="multiselect-combobox-helper-text"
                className="ui-field-control"
                type="text"
                value={helperText}
                placeholder="Optional helper text"
                onChange={(event) => setHelperText(event.target.value)}
              />
            ),
          },
          {
            id: 'multiselect-combobox-max-selected',
            label: 'Max selected',
            control: (
              <input
                id="multiselect-combobox-max-selected"
                className="ui-field-control"
                type="number"
                min={1}
                value={maxSelected}
                placeholder="No limit"
                onChange={(event) => setMaxSelected(event.target.value)}
              />
            ),
          },
        ],
      },
      {
        title: 'State',
        fields: [
          {
            id: 'multiselect-combobox-disabled',
            label: 'Disabled',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="multiselect-combobox-disabled"
                  type="checkbox"
                  checked={disabled}
                  onChange={(event) => setDisabled(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'multiselect-combobox-optional',
            label: 'Optional',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="multiselect-combobox-optional"
                  type="checkbox"
                  checked={optional}
                  onChange={(event) => setOptional(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'multiselect-combobox-tooltip',
            label: 'Tooltip',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="multiselect-combobox-tooltip"
                  type="checkbox"
                  checked={showTooltip}
                  onChange={(event) => setShowTooltip(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'multiselect-combobox-filterable',
            label: 'Filterable',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="multiselect-combobox-filterable"
                  type="checkbox"
                  checked={filterable}
                  onChange={(event) => setFilterable(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'multiselect-combobox-close-on-select',
            label: 'Close on select',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="multiselect-combobox-close-on-select"
                  type="checkbox"
                  checked={closeOnSelect}
                  onChange={(event) => setCloseOnSelect(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'multiselect-combobox-error',
            label: 'Error',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="multiselect-combobox-error"
                  type="checkbox"
                  checked={error}
                  onChange={(event) => setError(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
        ],
      },
    ],
    [
      closeOnSelect,
      disabled,
      error,
      filterable,
      helperText,
      highlightedState,
      label,
      maxSelected,
      optional,
      showTooltip,
    ],
  )

  return (
    <>
      <UiPreviewCanvas
        title="Multiselect combobox"
        description="Type-to-filter field that keeps every selection as a removable chip, built on the same chrome as Combobox and Select."
        stageClassName="ui-preview__stage--wide multiselect-combobox-showcase__stage-wrap"
      >
        <div className={`multiselect-combobox-showcase__sheet`}>
          <section className={`ui-showcase-live`}>
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              {highlightedState === 'default'
                ? 'Type to filter, then pick options. Highlighted state Default keeps this interactive; other states show that preset here.'
                : `Live preview is showing the ${MULTISELECT_COMBOBOX_PREVIEW_STATE_LABELS[highlightedState]} preset. Switch Highlighted state back to Default to interact.`}
            </p>
            <div
              className={`multiselect-combobox-showcase__live-grid${
                highlightedState === 'expanded'
                  ? ' multiselect-combobox-showcase__live-grid--expanded'
                  : ''
              }`}
            >
              {highlightedState === 'default' ? (
                <>
                  <MultiselectCombobox
                    label={label}
                    placeholder="Type to search"
                    value={procedures}
                    options={MULTISELECT_COMBOBOX_DEMO_OPTIONS}
                    maxSelected={parsedMaxSelected}
                    disabled={disabled}
                    optional={optional}
                    showTooltip={showTooltip}
                    filterable={filterable}
                    closeOnSelect={closeOnSelect}
                    error={error}
                    helperText={helperText || undefined}
                    onChange={setProcedures}
                  />
                  <MultiselectCombobox
                    label="Allergies"
                    placeholder="Type to search"
                    value={allergies}
                    options={ALLERGY_OPTIONS}
                    maxSelected={parsedMaxSelected}
                    disabled={disabled}
                    optional={optional}
                    showTooltip={showTooltip}
                    filterable={filterable}
                    closeOnSelect={closeOnSelect}
                    error={error}
                    helperText={helperText || undefined}
                    onChange={setAllergies}
                  />
                </>
              ) : (
                <MultiselectCombobox previewState={highlightedState} />
              )}
            </div>
          </section>

          <section className="multiselect-combobox-showcase__matrix">
            {MULTISELECT_COMBOBOX_PREVIEW_STATES.map((previewState) => (
              <div
                key={previewState}
                ref={(element) => {
                  rowRefs.current[previewState] = element
                }}
                className={`multiselect-combobox-showcase__row${
                  previewState === highlightedState
                    ? ' multiselect-combobox-showcase__row--selected'
                    : ''
                }${
                  previewState === 'expanded'
                    ? ' multiselect-combobox-showcase__row--expanded'
                    : ''
                }`}
              >
                <h4 className="multiselect-combobox-showcase__label">
                  {MULTISELECT_COMBOBOX_PREVIEW_STATE_LABELS[previewState]}
                </h4>
                <MultiselectCombobox previewState={previewState} />
              </div>
            ))}
          </section>
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel
        componentName="Multiselect combobox"
        sections={propertySections}
      />
    </>
  )
}

export default MultiselectComboboxShowcase
